import { ref, onScopeDispose } from 'vue'
import { v4 } from 'uuid'
import IntegrationService from '@/services/IntegrationService'

function openExternal(url) {
	if (window.todesktop) {
		window.todesktop.contents.openUrlInBrowser(url)
	} else {
		window.open(url, '_blank')
	}
}

export function usePlatformOauth({ user, stream, onReload }) {
	const facebookOauthState = ref({})
	const youtubeOauthState = ref({})
	const pendingTimers = new Set()

	onScopeDispose(() => {
		pendingTimers.forEach(clearTimeout)
		pendingTimers.clear()
	})

	function ensureFacebookState(account) {
		if (!account) return
		if (!facebookOauthState.value[account]) {
			facebookOauthState.value = {
				...facebookOauthState.value,
				[account]: { isLoading: false, errorMessage: '' },
			}
		}
	}

	function ensureYoutubeState(account) {
		if (!account) return
		if (!youtubeOauthState.value[account]) {
			youtubeOauthState.value = {
				...youtubeOauthState.value,
				[account]: { isLoading: false, errorMessage: '' },
			}
		}
	}

	function patchOauthState(bucketRef, key, patch) {
		const current = bucketRef.value[key] || { isLoading: false, errorMessage: '' }
		bucketRef.value = {
			...bucketRef.value,
			[key]: { ...current, ...patch },
		}
	}

	function getFacebookUrl(fbAccount) {
		const pathname = 'integrations/facebook/oauth'
		const connectId = v4()
		const userId = user.value._id || user.value.id
		const searchParams = `redirect=1&user=${userId}&stream=${stream.value._id}&connectId=${connectId}&fbAccount=${fbAccount}`
		const routeUrl = `${process.env.VUE_APP_APP_API_BASE}/${pathname}?${searchParams}`

		ensureFacebookState(fbAccount)
		patchOauthState(facebookOauthState, fbAccount, { isLoading: true, errorMessage: '' })
		listenAckEventResponse(connectId, fbAccount, onReload)
		openExternal(routeUrl)
	}

	function getYoutubeUrl(ytAccount) {
		const pathname = 'integrations/youtube/oauth'
		const connectId = v4()
		const userId = user.value._id || user.value.id
		const searchParams = `redirect=1&user=${userId}&stream=${stream.value._id}&connectId=${connectId}&ytAccount=${ytAccount}`
		const routeUrl = `${process.env.VUE_APP_APP_API_BASE}/${pathname}?${searchParams}`

		ensureYoutubeState(ytAccount)
		patchOauthState(youtubeOauthState, ytAccount, { isLoading: true, errorMessage: '' })
		listenAckEventResponseYT(connectId, ytAccount, onReload)
		openExternal(routeUrl)
	}

	async function listenAckEventResponse(key, fbAccount, callback) {
		if (!facebookOauthState.value[fbAccount]?.isLoading) return
		const ackRes = await IntegrationService.getAckByKey(key)
		if (ackRes && ackRes.success) {
			patchOauthState(facebookOauthState, fbAccount, {
				isLoading: false,
				errorMessage: ackRes?.data?.error ? ackRes?.data?.message : '',
			})
			await callback(ackRes.data)
		} else {
			const timer = setTimeout(() => {
				pendingTimers.delete(timer)
				listenAckEventResponse(key, fbAccount, callback)
			}, 4000)
			pendingTimers.add(timer)
		}
	}

	async function listenAckEventResponseYT(key, ytAccount, callback) {
		if (!youtubeOauthState.value[ytAccount]?.isLoading) return
		const ackRes = await IntegrationService.getAckByKey(key)
		if (ackRes && ackRes.success) {
			patchOauthState(youtubeOauthState, ytAccount, {
				isLoading: false,
				errorMessage: ackRes?.data?.error ? ackRes?.data?.message : '',
			})
			await callback(ackRes.data)
		} else {
			const timer = setTimeout(() => {
				pendingTimers.delete(timer)
				listenAckEventResponseYT(key, ytAccount, callback)
			}, 4000)
			pendingTimers.add(timer)
		}
	}

	return {
		facebookOauthState,
		youtubeOauthState,
		ensureFacebookState,
		ensureYoutubeState,
		getFacebookUrl,
		getYoutubeUrl,
	}
}
